import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  useTheme,
  useMediaQuery,
  CardContent,
  Grid,
  Stack,
  InputAdornment,
  Tooltip,
  Chip,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Publish as PublishIcon,
  Block as BlockIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  FilterAlt as FilterAltIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import clienteService, { Cliente, PARAMS_CLIENTE_OPERATIVO } from '../../services/clienteService';
import productoService, { Producto, PARAMS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import rutaService, { Ruta } from '../../services/rutaService';
import listaPrecioService, { ListaPrecio } from '../../services/listaPrecioService';
import vendedorService, { Vendedor } from '../../services/vendedorService';
import { catalogosService } from '../../services/catalogosService';
import AtributoSearch from '../../components/AtributoSearch';
import descuentoService, {
  DescuentoDto,
  DescuentoSearchParams,
  ESTADO_DESCUENTO_LABEL,
  EstadoDescuento,
  describirDimensiones,
  descuentoVencido,
  descuentoKeyId,
  hoyIso,
  normalizarEstadoDescuento,
  toEditSearch,
} from '../../services/descuentoService';
import { getApiErrorMessage } from '../../utils/apiError';
import { Clase, Linea, Ramo } from '../../types';

const formatFecha = (value?: string | null): string => {
  if (!value) return '—';
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('es-AR');
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s.slice(0, 10);
  return d.toLocaleDateString('es-AR');
};

const formatPorc = (value?: number | null): string => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n} %`;
};

const formatCantidadDesde = (value?: number | null): string => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n.toLocaleString('es-AR') : '—';
};

const ESTADO_COLOR = { P: 'warning', A: 'success', I: 'default' } as const;

interface FiltrosAvanzadosOferta {
  codigoLista: string;
  nroCliente: string;
  codigoRuta: string;
  nroVendedor: string;
  atributo1Cli: string;
  atributo2Cli: string;
  codigoArticulo: string;
  codigoLinea: string;
  codigoRamo: string;
  codigoClase: string;
  atributo1Sto: string;
  atributo2Sto: string;
}

const FILTROS_AVANZADOS_VACIOS: FiltrosAvanzadosOferta = {
  codigoLista: '',
  nroCliente: '',
  codigoRuta: '',
  nroVendedor: '',
  atributo1Cli: '',
  atributo2Cli: '',
  codigoArticulo: '',
  codigoLinea: '',
  codigoRamo: '',
  codigoClase: '',
  atributo1Sto: '',
  atributo2Sto: '',
};

const numeroFiltro = (value: string): number | undefined => {
  if (!value.trim()) return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

interface OfertaSearchFieldProps<T> {
  label: string;
  value: string;
  fetchOptions: (term: string) => Promise<T[]>;
  getOptionValue: (option: T) => string;
  getOptionLabel: (option: T) => string;
  onChange: (value: string) => void;
}

const OfertaSearchField = <T,>({
  label,
  value,
  fetchOptions,
  getOptionValue,
  getOptionLabel,
  onChange,
}: OfertaSearchFieldProps<T>) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedOption, setSelectedOption] = useState<T | null>(null);
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const requestId = React.useRef(0);
  const lastValue = React.useRef(value);

  useEffect(() => {
    if (value === lastValue.current) return;
    lastValue.current = value;
    setSelectedOption(null);
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const term = inputValue.trim();
    if (!open || selectedOption || !term) {
      setOptions([]);
      setError('');
      setLoading(false);
      return;
    }

    const id = ++requestId.current;
    const timeout = setTimeout(() => {
      setLoading(true);
      setError('');
      fetchOptions(term)
        .then((items) => {
          if (id === requestId.current) setOptions(items);
        })
        .catch((err: unknown) => {
          if (id !== requestId.current) return;
          setOptions([]);
          setError(getApiErrorMessage(err, `No se pudo buscar ${label.toLowerCase()}.`));
        })
        .finally(() => {
          if (id === requestId.current) setLoading(false);
        });
    }, 300);

    return () => {
      clearTimeout(timeout);
      requestId.current += 1;
    };
  }, [fetchOptions, inputValue, label, open, selectedOption]);

  return (
    <Autocomplete<T>
      fullWidth
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={selectedOption}
      inputValue={inputValue}
      loading={loading}
      filterOptions={(items) => items}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, selected) => getOptionValue(option) === getOptionValue(selected)}
      noOptionsText={error || (inputValue.trim() ? 'No se encontraron coincidencias.' : 'Escribí para buscar.')}
      onChange={(_, option) => {
        setSelectedOption(option);
        const nextValue = option ? getOptionValue(option) : '';
        lastValue.current = nextValue;
        setInputValue(option ? getOptionLabel(option) : '');
        onChange(nextValue);
      }}
      onInputChange={(_, nextInput, reason) => {
        if (reason === 'clear') {
          setInputValue('');
          setSelectedOption(null);
          setOptions([]);
          if (value) {
            lastValue.current = '';
            onChange('');
          }
          return;
        }
        if (reason !== 'input') return;
        setInputValue(nextInput);
        setSelectedOption(null);
        if (value) {
          lastValue.current = '';
          onChange('');
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={Boolean(error)}
          helperText={error || undefined}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      )}
    />
  );
};

const buscarClientesOferta = async (term: string): Promise<Cliente[]> => {
  const result = await clienteService.getClientes({
    searchTerm: term,
    pageNumber: 1,
    pageSize: 20,
    ...PARAMS_CLIENTE_OPERATIVO,
  });
  return result.items ?? [];
};

const buscarArticulosOferta = async (term: string): Promise<Producto[]> => {
  const result = await productoService.getProductos({
    searchTerm: term,
    pageNumber: 1,
    pageSize: 20,
    ...PARAMS_PRODUCTO_OPERATIVO,
  });
  return result.items ?? [];
};

const buscarRutasOferta = async (term: string): Promise<Ruta[]> => {
  const result = await rutaService.getRutasPaged({ searchTerm: term, pageNumber: 1, pageSize: 20 });
  return result.items ?? [];
};

const buscarListasOferta = async (term: string): Promise<ListaPrecio[]> => {
  const result = await listaPrecioService.getListaPrecios({ searchTerm: term, pageNumber: 1, pageSize: 20 });
  return result.items ?? [];
};

const buscarVendedoresOferta = async (term: string): Promise<Vendedor[]> => {
  const result = await vendedorService.getVendedoresPaged({ searchTerm: term, pageNumber: 1, pageSize: 20 });
  return result.items ?? [];
};

const buscarRamosOferta = async (term: string): Promise<Ramo[]> => {
  const result = await catalogosService.getRamos(1, 20, term);
  return result.items ?? [];
};

const buscarLineasOferta = async (term: string): Promise<Linea[]> => {
  const result = await catalogosService.getLineas(1, 20, term);
  return result.items ?? [];
};

const buscarClasesOferta = async (term: string): Promise<Clase[]> => {
  const result = await catalogosService.getClases(1, 20, term);
  return result.items ?? [];
};

/** Motivo por el que no se puede publicar hoy, o '' si se puede. */
const bloqueoPublicar = (d: DescuentoDto): string => {
  const hoy = hoyIso();
  const desde = String(d.fechaDesde ?? '').slice(0, 10);
  const hasta = String(d.fechaVigencia ?? '').slice(0, 10);
  if (desde && desde > hoy) return `Se podrá publicar a partir del ${formatFecha(desde)}`;
  if (hasta && hasta < hoy) return 'Vencido';
  return '';
};

const Descuentos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [items, setItems] = useState<DescuentoDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoDescuento | ''>('');
  const [filtrosAvanzados, setFiltrosAvanzados] = useState<FiltrosAvanzadosOferta>(
    FILTROS_AVANZADOS_VACIOS,
  );
  const [loading, setLoading] = useState(false);
  const filtrosAvanzadosActivos = Object.values(filtrosAvanzados).filter(Boolean).length;

  const fetchDescuentos = useCallback(
    async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
      try {
        setLoading(true);
        const params: DescuentoSearchParams = {
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search || undefined,
          estado: estadoFiltro || undefined,
          codigoLista: filtrosAvanzados.codigoLista.trim() || undefined,
          nroCliente: numeroFiltro(filtrosAvanzados.nroCliente),
          codigoRuta: numeroFiltro(filtrosAvanzados.codigoRuta),
          nroVendedor: numeroFiltro(filtrosAvanzados.nroVendedor),
          atributo1Cli: filtrosAvanzados.atributo1Cli.trim() || undefined,
          atributo2Cli: filtrosAvanzados.atributo2Cli.trim() || undefined,
          codigoArticulo: filtrosAvanzados.codigoArticulo.trim() || undefined,
          codigoLinea: filtrosAvanzados.codigoLinea.trim() || undefined,
          codigoRamo: filtrosAvanzados.codigoRamo.trim() || undefined,
          codigoClase: filtrosAvanzados.codigoClase.trim() || undefined,
          atributo1Sto: filtrosAvanzados.atributo1Sto.trim() || undefined,
          atributo2Sto: filtrosAvanzados.atributo2Sto.trim() || undefined,
        };
        let data = await descuentoService.getDescuentos(params);
        const vencidos = data.items.filter((item) => descuentoVencido(item));
        if (vencidos.length > 0) {
          const resultados = await Promise.allSettled(vencidos.map((item) => descuentoService.inactivar(item)));
          const inactivados = resultados.filter((result) => result.status === 'fulfilled').length;
          const errores = resultados.length - inactivados;
          if (errores > 0) {
            enqueueSnackbar(`No se pudieron inactivar automáticamente ${errores} oferta(s) vencida(s).`, {
              variant: 'error',
            });
          }
          if (inactivados > 0) data = await descuentoService.getDescuentos(params);
        }
        setItems(data.items);
        setTotalCount(data.totalCount);
      } catch (error: unknown) {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar ofertas'), { variant: 'error' });
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar, estadoFiltro, filtrosAvanzados, page, rowsPerPage, searchTerm],
  );

  useEffect(() => {
    fetchDescuentos(0, rowsPerPage, searchTerm);
    // Carga inicial
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchDescuentos(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, estadoFiltro, filtrosAvanzados]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchDescuentos(newPage, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchDescuentos(0, newRowsPerPage, searchTerm);
  };

  const actualizarFiltroAvanzado = (campo: keyof FiltrosAvanzadosOferta, value: string) => {
    setFiltrosAvanzados((actuales) => ({ ...actuales, [campo]: value }));
  };

  const limpiarFiltrosAvanzados = () => {
    setFiltrosAvanzados(FILTROS_AVANZADOS_VACIOS);
  };

  const handleEdit = (d: DescuentoDto) => {
    navigate(`/inventario/descuentos/editar?${toEditSearch(d)}`, { state: { descuento: d } });
  };

  const handleCambiarEstado = async (d: DescuentoDto, accion: 'publicar' | 'inactivar') => {
    if (accion === 'inactivar' && !window.confirm('¿Inactivar esta oferta? Deja de aplicarse en los documentos.')) {
      return;
    }
    try {
      if (accion === 'publicar') await descuentoService.publicar(d);
      else await descuentoService.inactivar(d);
      enqueueSnackbar(accion === 'publicar' ? 'Oferta publicada: ya está en vigencia.' : 'Oferta inactivada.', {
        variant: 'success',
      });
      fetchDescuentos(page, rowsPerPage, searchTerm);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo cambiar el estado de la oferta'), { variant: 'error' });
    }
  };

  const renderAcciones = (d: DescuentoDto) => {
    const estado = normalizarEstadoDescuento(d.estado);
    const bloqueo = estado === 'A' ? '' : bloqueoPublicar(d);
    return (
      <>
        <Tooltip title={estado === 'A' ? 'Ver' : 'Editar'}>
          <IconButton size="small" onClick={() => handleEdit(d)} color="primary">
            {estado === 'A' ? <VisibilityIcon /> : <EditIcon />}
          </IconButton>
        </Tooltip>
        {estado !== 'A' && (
          <Tooltip title={bloqueo || 'Publicar'}>
            <span>
              <IconButton
                size="small"
                color="success"
                disabled={Boolean(bloqueo)}
                onClick={() => handleCambiarEstado(d, 'publicar')}
              >
                <PublishIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {estado === 'A' && (
          <Tooltip title="Inactivar">
            <IconButton size="small" color="warning" onClick={() => handleCambiarEstado(d, 'inactivar')}>
              <BlockIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Eliminar">
          <IconButton size="small" onClick={() => handleDelete(d)} color="error">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </>
    );
  };

  const renderEstado = (d: DescuentoDto) => {
    const estado = normalizarEstadoDescuento(d.estado);
    return <Chip size="small" label={ESTADO_DESCUENTO_LABEL[estado]} color={ESTADO_COLOR[estado]} />;
  };

  const handleDelete = async (d: DescuentoDto) => {
    if (!window.confirm('¿Está seguro de eliminar esta oferta?')) return;
    try {
      await descuentoService.remove(d);
      enqueueSnackbar('Oferta eliminada exitosamente', { variant: 'success' });
      fetchDescuentos(page, rowsPerPage, searchTerm);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al eliminar la oferta'), { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Ofertas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Ofertas con vigencia por cliente, ruta, lista, artículo u otra dimensión, con tramos opcionales por cantidad. El backend define a quién aplica.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/inventario/descuentos/nuevo')}
            >
              Nueva oferta
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              select
              label="Estado"
              value={estadoFiltro}
              onChange={(e) => setEstadoFiltro(e.target.value as EstadoDescuento | '')}
              sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="P">Pendiente</MenuItem>
              <MenuItem value="A">Activo</MenuItem>
              <MenuItem value="I">Inactivo</MenuItem>
            </TextField>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Stack>

          <Accordion
            variant="outlined"
            disableGutters
            sx={{
              mb: 3,
              borderRadius: 2,
              '&:before': { display: 'none' },
              boxShadow: 'none',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 2,
                bgcolor: '#fafafa',
                minHeight: 56,
                '&.Mui-expanded': { minHeight: 56 },
                '& .MuiAccordionSummary-content': { my: 1.25 },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, pr: 1 }}>
                <FilterAltIcon color="primary" fontSize="small" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Filtros avanzados
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {filtrosAvanzadosActivos > 0
                      ? `${filtrosAvanzadosActivos} filtro(s) aplicado(s)`
                      : 'Filtrá por lista, cliente, artículo y otras dimensiones'}
                  </Typography>
                </Box>
                {filtrosAvanzadosActivos > 0 && (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={filtrosAvanzadosActivos}
                    sx={{ ml: 1 }}
                  />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 1.5, sm: 2 }, pt: 0, pb: 2 }}>
              <Stack spacing={2}>
                <Accordion
                  defaultExpanded
                  disableGutters
                  variant="outlined"
                  sx={{ borderRadius: 2, '&:before': { display: 'none' }, boxShadow: 'none' }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 52 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Cliente y condiciones comerciales
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Acotá la oferta por lista, cliente, ruta, vendedor o atributos del cliente.
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6} md={3}>
                        <OfertaSearchField<ListaPrecio>
                          label="Lista de precios"
                          value={filtrosAvanzados.codigoLista}
                          fetchOptions={buscarListasOferta}
                          getOptionValue={(lista) => lista.codigoLista}
                          getOptionLabel={(lista) =>
                            `${lista.codigoLista}${lista.descripcion ? ` - ${lista.descripcion}` : ''}`
                          }
                          onChange={(codigo) => actualizarFiltroAvanzado('codigoLista', codigo)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <OfertaSearchField<Cliente>
                          label="Cliente"
                          value={filtrosAvanzados.nroCliente}
                          fetchOptions={buscarClientesOferta}
                          getOptionValue={(cliente) => String(cliente.nroCliente)}
                          getOptionLabel={(cliente) => {
                            const nombre = cliente.nombre?.trim() || cliente.razonSocial?.trim();
                            return nombre ? `${cliente.nroCliente} - ${nombre}` : String(cliente.nroCliente);
                          }}
                          onChange={(nroCliente) => actualizarFiltroAvanzado('nroCliente', nroCliente)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <OfertaSearchField<Ruta>
                          label="Ruta"
                          value={filtrosAvanzados.codigoRuta}
                          fetchOptions={buscarRutasOferta}
                          getOptionValue={(ruta) => String(ruta.codigoRuta ?? '')}
                          getOptionLabel={(ruta) =>
                            `${ruta.codigoRuta ?? ''}${ruta.nombreRuta ? ` - ${ruta.nombreRuta}` : ''}`
                          }
                          onChange={(codigoRuta) => actualizarFiltroAvanzado('codigoRuta', codigoRuta)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <OfertaSearchField<Vendedor>
                          label="Vendedor"
                          value={filtrosAvanzados.nroVendedor}
                          fetchOptions={buscarVendedoresOferta}
                          getOptionValue={(vendedor) => String(vendedor.nroVendedor)}
                          getOptionLabel={(vendedor) =>
                            `${vendedor.nroVendedor}${vendedor.nombreVendedor ? ` - ${vendedor.nombreVendedor}` : ''}`
                          }
                          onChange={(nroVendedor) => actualizarFiltroAvanzado('nroVendedor', nroVendedor)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <AtributoSearch
                          origen="cliente"
                          seleccion={[filtrosAvanzados.atributo1Cli, filtrosAvanzados.atributo2Cli]}
                          onChange={(slot, atributo) =>
                            actualizarFiltroAvanzado(slot === 0 ? 'atributo1Cli' : 'atributo2Cli', atributo)
                          }
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  defaultExpanded
                  disableGutters
                  variant="outlined"
                  sx={{ borderRadius: 2, '&:before': { display: 'none' }, boxShadow: 'none' }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 52 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Artículo y clasificación
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Buscá por artículo, línea, ramo, clase o atributos del artículo.
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6} md={3}>
                        <OfertaSearchField<Producto>
                          label="Artículo"
                          value={filtrosAvanzados.codigoArticulo}
                          fetchOptions={buscarArticulosOferta}
                          getOptionValue={(articulo) => articulo.codigoArticulo}
                          getOptionLabel={(articulo) =>
                            `${articulo.codigoArticulo}${articulo.descripcion ? ` - ${articulo.descripcion}` : ''}`
                          }
                          onChange={(codigo) => actualizarFiltroAvanzado('codigoArticulo', codigo)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <OfertaSearchField<Linea>
                          label="Línea"
                          value={filtrosAvanzados.codigoLinea}
                          fetchOptions={buscarLineasOferta}
                          getOptionValue={(linea) => linea.codigoLinea}
                          getOptionLabel={(linea) => `${linea.codigoLinea} - ${linea.nombreLinea}`}
                          onChange={(codigo) => {
                            actualizarFiltroAvanzado('codigoLinea', codigo);
                            actualizarFiltroAvanzado('codigoClase', '');
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <OfertaSearchField<Ramo>
                          label="Ramo"
                          value={filtrosAvanzados.codigoRamo}
                          fetchOptions={buscarRamosOferta}
                          getOptionValue={(ramo) => ramo.codigoRamo}
                          getOptionLabel={(ramo) => `${ramo.codigoRamo} - ${ramo.nombreRamo}`}
                          onChange={(codigo) => {
                            actualizarFiltroAvanzado('codigoRamo', codigo);
                            actualizarFiltroAvanzado('codigoLinea', '');
                            actualizarFiltroAvanzado('codigoClase', '');
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <OfertaSearchField<Clase>
                          label="Clase"
                          value={filtrosAvanzados.codigoClase}
                          fetchOptions={buscarClasesOferta}
                          getOptionValue={(clase) => clase.codigoClase}
                          getOptionLabel={(clase) => `${clase.codigoClase} - ${clase.nombreClase}`}
                          onChange={(codigo) => actualizarFiltroAvanzado('codigoClase', codigo)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <AtributoSearch
                          origen="articulo"
                          seleccion={[filtrosAvanzados.atributo1Sto, filtrosAvanzados.atributo2Sto]}
                          onChange={(slot, atributo) =>
                            actualizarFiltroAvanzado(slot === 0 ? 'atributo1Sto' : 'atributo2Sto', atributo)
                          }
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {filtrosAvanzadosActivos > 0 && (
                  <Stack direction="row" justifyContent="flex-end">
                    <Button size="small" color="inherit" onClick={limpiarFiltrosAvanzados}>
                      Limpiar filtros avanzados
                    </Button>
                  </Stack>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <Typography color="text.secondary">Cargando...</Typography>
            </Box>
          ) : isMobile ? (
            <Stack spacing={2}>
              {items.map((d) => (
                <Card key={descuentoKeyId(d)} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" fontWeight={600}>
                        {formatPorc(d.porcDesc)}
                      </Typography>
                      {renderEstado(d)}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {describirDimensiones(d)}
                      {Number(d.cantidadDesde) > 0 ? ` · Desde ${formatCantidadDesde(d.cantidadDesde)} u.` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Desde: {formatFecha(d.fechaDesde)} · Hasta: {formatFecha(d.fechaVigencia)}
                      {d.fechaAprobacion ? ` · Publicado: ${formatFecha(d.fechaAprobacion)}` : ''}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {renderAcciones(d)}
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Aplica a</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Cant. desde
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      %
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Desde</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Hasta</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Publicado</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((d) => (
                    <TableRow key={descuentoKeyId(d)} hover>
                      <TableCell>{describirDimensiones(d)}</TableCell>
                      <TableCell align="right">{formatCantidadDesde(d.cantidadDesde)}</TableCell>
                      <TableCell align="right">{formatPorc(d.porcDesc)}</TableCell>
                      <TableCell>{formatFecha(d.fechaDesde)}</TableCell>
                      <TableCell>{formatFecha(d.fechaVigencia)}</TableCell>
                      <TableCell>{renderEstado(d)}</TableCell>
                      <TableCell>{formatFecha(d.fechaAprobacion)}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        {renderAcciones(d)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                          No hay ofertas para mostrar.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Descuentos;